/**
 * ECharts 图表主题配置
 * 根据当前主题（浅色/深色）提供最佳的图表配置
 */

/**
 * 获取图表颜色（按索引）
 * @param index 颜色索引
 * @param theme 当前主题
 */
export function getChartColor(index: number, theme: 'light' | 'dark' = 'light'): string {
  const root = document.documentElement
  const chartColors = Array.from({ length: 8 }, (_, i) =>
    getComputedStyle(root).getPropertyValue(`--chart-color-${i + 1}`).trim()
  )
  return chartColors[index % chartColors.length] || (theme === 'dark' ? '#60A5FA' : '#3B82F6')
}

/**
 * 获取ECharts通用配置（根据主题）
 * @param theme 当前主题
 */
export function getCommonEChartsTheme(theme: 'light' | 'dark' = 'light') {
  const textColor = theme === 'dark' ? '#9CA3AF' : '#64748B'
  const gridColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'
  const borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0'
  const tooltipBg = theme === 'dark' ? '#1F2937' : '#FFFFFF'
  const tooltipText = theme === 'dark' ? '#F3F4F6' : '#1E293B'

  return {
    // Tooltip 配置
    tooltip: {
      backgroundColor: tooltipBg,
      borderColor: borderColor,
      borderWidth: 1,
      textStyle: {
        color: tooltipText,
        fontSize: 12
      },
      padding: [8, 12],
      extraCssText: theme === 'dark'
        ? 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);'
        : 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);'
    },

    // Legend 配置
    legend: {
      textStyle: {
        color: textColor,
        fontSize: 12
      },
      pageTextStyle: {
        color: textColor
      }
    },

    // Grid 配置
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '40px',
      containLabel: true
    },

    // X轴配置
    xAxis: {
      axisLine: {
        lineStyle: {
          color: borderColor
        }
      },
      axisLabel: {
        color: textColor,
        fontSize: 12
      },
      splitLine: {
        lineStyle: {
          color: gridColor,
          type: 'dashed' as const
        }
      }
    },

    // Y轴配置
    yAxis: {
      axisLine: {
        lineStyle: {
          color: borderColor
        }
      },
      axisLabel: {
        color: textColor,
        fontSize: 12
      },
      splitLine: {
        lineStyle: {
          color: gridColor,
          type: 'dashed' as const
        }
      }
    }
  }
}

/**
 * 为饼图创建边框颜色（匹配卡片背景）
 * @param theme 当前主题
 */
export function getPieChartBorderColor(theme: 'light' | 'dark' = 'light'): string {
  return theme === 'dark' ? '#141414' : '#FFFFFF'
}

/**
 * 获取区域填充渐变（用于折线图）
 * @param baseColor 基础颜色
 * @param theme 当前主题
 */
export function getAreaGradient(baseColor: string, theme: 'light' | 'dark' = 'light') {
  // 深色主题使用更高的透明度，浅色主题使用较低的透明度
  const startOpacity = theme === 'dark' ? 'cc' : 'aa'
  const endOpacity = '00'

  return {
    type: 'linear' as const,
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: baseColor + startOpacity },
      { offset: 1, color: baseColor + endOpacity }
    ]
  }
}
